// InventorySettingsPage.js
// Admin page for managing Status Codes (color codes) and Unit Categories.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, X } from 'lucide-react';
import { api } from '../scripts/api';

// ── Shared helpers ─────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid var(--card-border)' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Msg({ text, error }) {
  if (!text) return null;
  return (
    <div style={{ fontSize: 13, marginBottom: 10, color: error ? '#c00' : '#060' }}>{text}</div>
  );
}

// ── Status Codes ───────────────────────────────────────────────────────────

const EMPTY_STATUS = { id: '', label: '', hex_color: '#4183e6' };

function StatusCodesSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [editId, setEditId] = useState(null);   // row id being edited inline
  const [editDraft, setEditDraft] = useState({});
  const [adding, setAdding] = useState(false);
  const [newForm, setNewForm] = useState(EMPTY_STATUS);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/status-codes');
      setRows(data);
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Failed to load status codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const startEdit = (row) => {
    setEditId(row.id);
    setEditDraft({ label: row.label, hex_color: row.hex_color || '#cccccc' });
  };

  const saveEdit = async () => {
    setBusy(true); setMsg('');
    try {
      await api.patch(`/admin/status-codes/${editId}`, editDraft);
      setEditId(null);
      setMsg('Saved.');
      await reload();
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    if (!newForm.id && newForm.id !== 0) return setMsg('Code (number) is required');
    if (!newForm.label) return setMsg('Label is required');
    setBusy(true); setMsg('');
    try {
      await api.post('/admin/status-codes', { id: Number(newForm.id), label: newForm.label, hex_color: newForm.hex_color });
      setAdding(false);
      setNewForm(EMPTY_STATUS);
      setMsg('Status code added.');
      await reload();
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Add failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete status code ${id}? Units using this code will remain but lose their status label.`)) return;
    setBusy(true); setMsg('');
    try {
      await api.delete(`/admin/status-codes/${id}`);
      setMsg('Deleted.');
      await reload();
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Status Codes">
      <p style={{ fontSize: 13, opacity: 0.65, marginBottom: 16, marginTop: 0 }}>
        Status codes (color codes) define the lifecycle stage of an inventory unit.
        Each code has a display color used throughout the app.
      </p>

      <Msg text={msg} error={msg.includes('fail') || msg.includes('Error')} />

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--card-border)', textAlign: 'left' }}>
              <th style={{ padding: '6px 10px', fontWeight: 700, width: 60 }}>Code</th>
              <th style={{ padding: '6px 10px', fontWeight: 700, width: 60 }}>Color</th>
              <th style={{ padding: '6px 10px', fontWeight: 700 }}>Label</th>
              <th style={{ padding: '6px 10px', fontWeight: 700, width: 160 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} style={{ padding: 12, opacity: 0.5 }}>Loading…</td></tr>
            )}
            {!loading && rows.map((row) => (
              <tr key={row.id} style={{ borderBottom: '1px solid var(--card-border)' }}>
                <td style={{ padding: '8px 10px', fontWeight: 700 }}>{row.id}</td>
                <td style={{ padding: '8px 10px' }}>
                  {editId === row.id ? (
                    <input
                      type="color"
                      value={editDraft.hex_color || '#cccccc'}
                      onChange={(e) => setEditDraft((d) => ({ ...d, hex_color: e.target.value }))}
                      style={{ width: 40, height: 28, border: 'none', padding: 0, cursor: 'pointer', borderRadius: 4 }}
                    />
                  ) : (
                    <div style={{
                      width: 28, height: 28, borderRadius: 6,
                      background: row.hex_color || '#ccc',
                      border: '1px solid rgba(0,0,0,0.15)',
                    }} />
                  )}
                </td>
                <td style={{ padding: '8px 10px' }}>
                  {editId === row.id ? (
                    <input
                      className="input"
                      value={editDraft.label}
                      onChange={(e) => setEditDraft((d) => ({ ...d, label: e.target.value }))}
                      style={{ width: '100%' }}
                      onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
                      autoFocus
                    />
                  ) : (
                    <span>{row.label}</span>
                  )}
                </td>
                <td style={{ padding: '8px 10px' }}>
                  {editId === row.id ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn" style={{ fontSize: 12, padding: '4px 10px' }} onClick={saveEdit} disabled={busy}>Save</button>
                      <button className="btn-outline" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setEditId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-outline" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => startEdit(row)} disabled={busy}>Edit</button>
                      <details style={{ position: 'relative', display: 'inline-block' }}>
                        <summary style={{ cursor: 'pointer', listStyle: 'none', padding: '3px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12, opacity: 0.45 }}>···</summary>
                        <div style={{ position: 'absolute', right: 0, top: '100%', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 10, minWidth: 140, padding: 4 }}>
                          <button
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px', fontSize: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#c00' }}
                            onClick={() => { document.activeElement.blur(); handleDelete(row.id); }}
                            disabled={busy}
                          >
                            Delete
                          </button>
                        </div>
                      </details>
                    </div>
                  )}
                </td>
              </tr>
            ))}

            {/* Add row */}
            {adding && (
              <tr style={{ borderBottom: '1px solid var(--card-border)', background: 'rgba(59,130,246,0.04)' }}>
                <td style={{ padding: '8px 10px' }}>
                  <input
                    className="input"
                    type="number"
                    placeholder="Code #"
                    value={newForm.id}
                    onChange={(e) => setNewForm((f) => ({ ...f, id: e.target.value }))}
                    style={{ width: 70 }}
                  />
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <input
                    type="color"
                    value={newForm.hex_color}
                    onChange={(e) => setNewForm((f) => ({ ...f, hex_color: e.target.value }))}
                    style={{ width: 40, height: 28, border: 'none', padding: 0, cursor: 'pointer', borderRadius: 4 }}
                  />
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <input
                    className="input"
                    placeholder="Label"
                    value={newForm.label}
                    onChange={(e) => setNewForm((f) => ({ ...f, label: e.target.value }))}
                    style={{ width: '100%' }}
                    onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                    autoFocus
                  />
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn" style={{ fontSize: 12, padding: '4px 10px' }} onClick={handleAdd} disabled={busy}>Add</button>
                    <button className="btn-outline" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => { setAdding(false); setNewForm(EMPTY_STATUS); }}>Cancel</button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!adding && (
        <button className="btn-outline" style={{ marginTop: 12, fontSize: 13 }} onClick={() => setAdding(true)}>
          <Plus size={14} style={{ marginRight: 6 }} />
          Add Status Code
        </button>
      )}
    </Section>
  );
}

// ── Unit Categories ────────────────────────────────────────────────────────

const DEFAULT_CATEGORIES = [
  { v: 'refuse',        label: 'Refuse' },
  { v: 'municipalequip',label: 'Municipal Equipment' },
  { v: 'municipalparts',label: 'Municipal Parts' },
  { v: 'landfill',      label: 'Landfill' },
  { v: 'sewer',         label: 'Sewer Water' },
  { v: 'sweeper',       label: 'Sweeper' },
  { v: 'mrc',           label: 'MRC' },
  { v: 'rail',          label: 'Rail' },
  { v: 'snow',          label: 'Sander, Spreader, Plows' },
  { v: 'tireshop',      label: 'Tire Shop' },
  { v: 'foundations',   label: 'Foundations' },
  { v: 'agriculture',   label: 'Agriculture' },
  { v: 'carts',         label: 'Carts' },
  { v: 'other',         label: 'Other' },
];

const CATS_KEY = 'sna_unit_categories_v1';

function loadCategories() {
  try {
    const raw = localStorage.getItem(CATS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return DEFAULT_CATEGORIES;
}

function CategoriesSection() {
  const [cats, setCats] = useState(loadCategories);
  const [newCat, setNewCat] = useState({ v: '', label: '' });
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState('');

  const save = (updated) => {
    setCats(updated);
    localStorage.setItem(CATS_KEY, JSON.stringify(updated));
  };

  const handleAdd = () => {
    if (!newCat.v || !newCat.label) return setMsg('Both key and label required');
    if (cats.find((c) => c.v === newCat.v)) return setMsg('Key already exists');
    save([...cats, newCat]);
    setNewCat({ v: '', label: '' });
    setAdding(false);
    setMsg('Category added.');
  };

  const handleDelete = (v) => {
    if (!window.confirm(`Remove category "${v}"? Units using this category will keep the value.`)) return;
    save(cats.filter((c) => c.v !== v));
    setMsg('Removed.');
  };

  const handleLabelEdit = (v, label) => {
    save(cats.map((c) => (c.v === v ? { ...c, label } : c)));
  };

  return (
    <Section title="Unit Categories">
      <p style={{ fontSize: 13, opacity: 0.65, marginBottom: 16, marginTop: 0 }}>
        Categories classify what type of equipment a unit is. These appear as dropdown options on inventory records.
        Changes are stored locally and applied on next build/deploy.
      </p>

      <Msg text={msg} error={msg.includes('required') || msg.includes('exists')} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
        {cats.map((cat) => (
          <div key={cat.v} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8 }}>
            <span style={{ fontSize: 11, opacity: 0.45, fontFamily: 'monospace', minWidth: 100 }}>{cat.v}</span>
            <input
              className="input"
              value={cat.label}
              onChange={(e) => handleLabelEdit(cat.v, e.target.value)}
              style={{ flex: 1, fontSize: 13 }}
            />
            <details style={{ position: 'relative', display: 'inline-block' }}>
              <summary style={{ cursor: 'pointer', listStyle: 'none', padding: '3px 6px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12, opacity: 0.4 }}>···</summary>
              <div style={{ position: 'absolute', right: 0, top: '100%', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 10, minWidth: 120, padding: 4 }}>
                <button
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px', fontSize: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#c00' }}
                  onClick={() => { document.activeElement.blur(); handleDelete(cat.v); }}
                >
                  Delete
                </button>
              </div>
            </details>
          </div>
        ))}
      </div>

      {adding ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 3 }}>Key (no spaces)</div>
            <input className="input" placeholder="e.g. forestry" value={newCat.v} onChange={(e) => setNewCat((f) => ({ ...f, v: e.target.value.toLowerCase().replace(/\s/g, '') }))} />
          </div>
          <div>
            <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 3 }}>Display Label</div>
            <input className="input" placeholder="e.g. Forestry Equipment" value={newCat.label} onChange={(e) => setNewCat((f) => ({ ...f, label: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && handleAdd()} />
          </div>
          <button className="btn" style={{ fontSize: 13 }} onClick={handleAdd}>Add</button>
          <button className="btn-outline" style={{ fontSize: 13 }} onClick={() => { setAdding(false); setNewCat({ v: '', label: '' }); }}>Cancel</button>
        </div>
      ) : (
        <button className="btn-outline" style={{ marginTop: 12, fontSize: 13 }} onClick={() => setAdding(true)}>
          <Plus size={14} style={{ marginRight: 6 }} />
          Add Category
        </button>
      )}
    </Section>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function InventorySettingsPage() {
  const nav = useNavigate();

  return (
    <div className="container">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <button className="btn-outline" style={{ fontSize: 13, padding: '5px 10px' }} onClick={() => nav('/admin')}>
          <ChevronLeft size={14} />
        </button>
        <div>
          <div style={{ fontWeight: 900, fontSize: 20 }}>Inventory Settings</div>
          <div style={{ fontSize: 12, opacity: 0.5 }}>Status codes, categories, and inventory defaults</div>
        </div>
      </div>

      <div className="card">
        <StatusCodesSection />
        <CategoriesSection />
      </div>
    </div>
  );
}
